import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import * as api from '../api/endpoints';

const iso = (d) => { const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000); return z.toISOString().slice(0, 10); };
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };
const t = (s) => (s ? new Date(s).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—');
const dayLabel = (s) => new Date(`${s}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
const ago = (s) => {
  if (!s) return '';
  const m = Math.max(0, Math.round((Date.now() - new Date(s).getTime()) / 60000));
  return m < 1 ? 'just now' : m < 60 ? `${m} min ago` : `${Math.floor(m / 60)}h ${m % 60}m ago`;
};

const STATUS = {
  online: ['Online', '#7be0a4'],
  away: ['Away / closed', '#ffcf7a'],
  done: ['Day finished', '#aab7c4'],
  done_no_times: ['Report (no times)', '#aab7c4'],
  no_report: ['NO REPORT', '#ff8a8a'],
  no_activity: ['No activity', '#ff8a8a'],
};

const th = { textAlign: 'left', padding: '4px 8px', fontSize: '0.72rem', color: '#aab7c4', fontWeight: 600, whiteSpace: 'nowrap' };
const td = { padding: '5px 8px', fontSize: '0.82rem', borderTop: '1px solid var(--border-subtle)', verticalAlign: 'top' };

/** Admin-only team attendance: who is online, work-day times, missing reports, late starts, outside-office days. */
const AttendanceModal = ({ onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(daysAgo(6));
  const [dateTo, setDateTo] = useState(iso(new Date()));
  const [person, setPerson] = useState('');
  const [onlyIssues, setOnlyIssues] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.getAttendance({ dateFrom, dateTo })
      .then(setData)
      .catch(() => toast.error('Could not load attendance.'))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const i = setInterval(load, 60000); return () => clearInterval(i); }, [load]);

  const rows = useMemo(() => {
    if (!data) return [];
    return data.rows.filter((r) => (!person || String(r.employee_id) === person)
      && (!onlyIssues || r.status === 'no_report' || r.status === 'no_activity' || r.late_minutes > 0 || r.start_outside_office || r.finish_outside_office));
  }, [data, person, onlyIssues]);

  const preset = (from, to) => { setDateFrom(from); setDateTo(to); };
  const people = data ? data.summary : [];
  const today = iso(new Date());

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 1000, width: '95vw' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ margin: 0 }}>Team attendance</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        {data && (
          <div style={{ marginBottom: '0.7rem' }}>
            <div className="section-title" style={{ marginTop: 0 }}>Today ({dayLabel(data.today)})</div>
            {data.online_now.length === 0 ? (
              <div style={{ color: '#aab7c4', fontSize: '0.85rem' }}>Nobody has opened the helpdesk yet today.</div>
            ) : (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {data.online_now.map((r) => (
                  <div key={r.employee_id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '0.4rem 0.7rem', fontSize: '0.82rem' }}>
                    <strong>{r.name}</strong>{' '}
                    <span style={{ color: STATUS[r.status][1], fontWeight: 700 }}>{STATUS[r.status][0]}</span>
                    <div style={{ color: '#aab7c4' }}>
                      since {t(r.started_at)}{r.duration ? ` · ${r.duration}` : ''} · seen {ago(r.last_seen_at)}
                      {r.start_outside_office && <span style={{ color: '#ffcf7a', fontWeight: 700 }}> · OUTSIDE OFFICE</span>}
                      {r.late_minutes > 0 && <span style={{ color: '#ffcf7a' }}> · late {r.late_minutes} min</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.6rem', alignItems: 'center' }}>
          {[['Today', today, today], ['Last 7 days', daysAgo(6), today], ['Last 30 days', daysAgo(29), today], ['Last 90 days', daysAgo(89), today]].map(([label, f, to]) => (
            <button key={label} type="button" className={dateFrom === f && dateTo === to ? 'btn-primary' : 'btn-secondary'}
              style={{ padding: '4px 10px', fontSize: '0.8rem' }} onClick={() => preset(f, to)}>{label}</button>
          ))}
          <input type="date" className="form-input" style={{ maxWidth: 150 }} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} aria-label="From date" />
          <input type="date" className="form-input" style={{ maxWidth: 150 }} value={dateTo} onChange={(e) => setDateTo(e.target.value)} aria-label="To date" />
          <select className="form-select" style={{ maxWidth: 190 }} value={person} onChange={(e) => setPerson(e.target.value)} aria-label="Employee">
            <option value="">All employees</option>
            {people.map((p) => <option key={p.employee_id} value={p.employee_id}>{p.name}</option>)}
          </select>
          <label style={{ fontSize: '0.82rem', display: 'flex', gap: 5, alignItems: 'center' }}>
            <input type="checkbox" checked={onlyIssues} onChange={(e) => setOnlyIssues(e.target.checked)} /> Only issues
          </label>
          <button type="button" className="btn-secondary" style={{ padding: '4px 10px', fontSize: '0.8rem' }} onClick={load}>Refresh</button>
        </div>

        {data && people.length > 0 && (
          <div style={{ overflowX: 'auto', marginBottom: '0.7rem' }}>
            <div className="section-title" style={{ marginTop: 0 }}>Summary for the period</div>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead><tr>
                <th style={th}>Employee</th><th style={th}>Days</th><th style={th}>Hours</th><th style={th}>Avg/day</th>
                <th style={th}>Reports</th><th style={th}>Missing reports</th><th style={th}>No activity</th><th style={th}>Late starts</th><th style={th}>Outside office</th>
              </tr></thead>
              <tbody>
                {people.map((p) => (
                  <tr key={p.employee_id}>
                    <td style={td}><strong>{p.name}</strong></td>
                    <td style={td}>{p.days}</td><td style={td}>{p.hours}h</td><td style={td}>{p.avg_hours}h</td>
                    <td style={td}>{p.reports}</td>
                    <td style={{ ...td, color: p.missing_reports ? '#ff8a8a' : undefined, fontWeight: p.missing_reports ? 700 : 400 }}>{p.missing_reports}</td>
                    <td style={{ ...td, color: p.no_activity ? '#ff8a8a' : undefined }}>{p.no_activity}</td>
                    <td style={{ ...td, color: p.late_starts ? '#ffcf7a' : undefined }}>{p.late_starts}</td>
                    <td style={{ ...td, color: p.outside_office ? '#ffcf7a' : undefined }}>{p.outside_office}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ fontSize: '0.72rem', color: '#aab7c4', marginTop: 4 }}>
              A day with no report counts as a half day ({data.half_day_hours}h). Late = first open after {data.work_start_time}. Weekends are not counted as missed.
            </div>
          </div>
        )}

        <div style={{ color: '#aab7c4', fontSize: '0.8rem', marginBottom: '0.3rem' }}>{loading && !data ? 'Loading…' : `${rows.length} row${rows.length === 1 ? '' : 's'}`}</div>
        <div style={{ maxHeight: '42vh', overflow: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr>
              <th style={th}>Date</th><th style={th}>Employee</th><th style={th}>Started</th><th style={th}>Finished</th>
              <th style={th}>Time</th><th style={th}>Status</th><th style={th}>Notes</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => {
                const st = STATUS[r.status] || [r.status, '#aab7c4'];
                return (
                  <tr key={`${r.date}-${r.employee_id}`}>
                    <td style={td}>{dayLabel(r.date)}</td>
                    <td style={td}><strong>{r.name}</strong></td>
                    <td style={td}>{t(r.started_at)}{r.late_minutes > 0 && <span style={{ color: '#ffcf7a' }}> (+{r.late_minutes}m)</span>}</td>
                    <td style={td}>{r.status === 'no_report' ? <span style={{ color: '#ff8a8a' }}>not submitted</span> : t(r.finished_at)}</td>
                    <td style={td}>{r.duration || '—'}</td>
                    <td style={{ ...td, color: st[1], fontWeight: 700 }}>{st[0]}</td>
                    <td style={{ ...td, color: '#aab7c4' }}>
                      {r.flag}{r.flag && (r.start_ip || r.finish_ip) ? ' · ' : ''}
                      {r.start_ip ? `in ${r.start_ip}${r.start_outside_office ? ' (outside office)' : ''}` : ''}
                      {r.finish_ip ? ` · out ${r.finish_ip}${r.finish_outside_office ? ' (outside office)' : ''}` : ''}
                    </td>
                  </tr>
                );
              })}
              {!loading && rows.length === 0 && <tr><td style={td} colSpan={7}>Nothing to show for these filters.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AttendanceModal;
